import type {
  NewPaymentMethodFn,
  SavePaymentMethodFn,
} from '@/features/billing'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { PaymentMethodSelectorViewModel } from '@/features/billing/payment-methods/PaymentMethodSelectorViewModel'
import { type Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { observable, runInAction } from 'mobx'

/**
 * View model for the payment method selector dialog.
 */
export class PaymentMethodSelectorDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<BillingMethod>({
    abort$: this.modal.close$,
  })

  /**
   * The selector of the current prompt.
   */
  @observable accessor selectorVm: PaymentMethodSelectorViewModel | null = null

  constructor(
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _newPaymentMethod: NewPaymentMethodFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Shows the selector and resolves with the selected payment method, or
   * `null` when the dialog is closed first.
   */
  async prompt(
    member: Member,
    team: Team | null,
    currentPaymentMethod: BillingMethod | null,
    onSave: SavePaymentMethodFn | null,
    signal?: AbortSignal,
  ) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.selectorVm = new PaymentMethodSelectorViewModel(
        member,
        team,
        currentPaymentMethod,
        onSave,
        (selectorSignal) =>
          this._newPaymentMethod(
            member,
            team,
            AbortSignal.any(
              signal ? [signal, selectorSignal] : [selectorSignal],
            ),
          ),
        this._billingProfileStore,
        this._createAlertErrorHandler,
        (method) => this._prompt.respond(method),
      )
      this.modal.show()
    })

    const method = await responsePromise
    this.modal.close()
    return method
  }
}
